import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CardContent,
  Input,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  CssBaseline,
  Select,
  Option,
  FormLabel,
} from '@mui/joy';
import {
  Paper,
  Container,
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { 
  getAllSettings,
  updateSettingById,
} from '../../main/API/settings';
import { readAccounts } from '../../main/API/account';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import AccountOptionList from '../../main/widgets/AccountOptionList';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';

export default function PaymentSettingsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [allSettings, setAllSettings] = useState([]);
  const [editingValues, setEditingValues] = useState({});
  const [initialLoad, setInitialLoad] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState('paymob');
  const [savingAll, setSavingAll] = useState(false);
  
  // Account selection states
  const [paymobAccount, setPaymobAccount] = useState(null);
  const [thawaniAccount, setThawaniAccount] = useState(null);
  const [accountsList, setAccountsList] = useState([]);
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const [paymobMethodIdsError, setPaymobMethodIdsError] = useState(null);

  const loadAllSettings = useCallback(async () => {
    console.log('PaymentSettingsScreen: loadAllSettings called');
    if (!hasPermission(PERMISSIONS.SETTINGS_LIST)) {
      console.warn('User does not have permission to access settings');
      setAllSettings([]);
      return;
    }

    setSettingsLoading(true);
    try {
      const settings = await getAllSettings();
      setAllSettings(Array.isArray(settings) ? settings : []);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setResponseMessage(t('settingsPages.common.loadFailed'));
      setFailedNotification(true);
      setAllSettings([]);
    } finally {
      setSettingsLoading(false);
    }
  }, [hasPermission, t]);

  useEffect(() => {
    if (!initialLoad) {
      console.log('PaymentSettingsScreen: Initial load starting');
      setInitialLoad(true);
      loadAllSettings();
    }
  }, [initialLoad, loadAllSettings]);

  // Fetch accounts list
  const loadAccounts = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.ACCOUNTS_LIST) && !hasPermission(PERMISSIONS.ACCOUNTS_VIEW)) {
      setAccountsList([]);
      return [];
    }
    try {
      const response = await readAccounts('', '');
      const results = response && response.data && response.data.results;
      const accounts = Array.isArray(results) ? results : [];
      setAccountsList(accounts);
      return accounts;
    } catch (error) {
      console.error('Failed to load accounts:', error);
      setAccountsList([]);
      return [];
    }
  }, [hasPermission]);

  // Load accounts on initial mount
  useEffect(() => {
    if (accountsList.length === 0) {
      loadAccounts();
    }
  }, [loadAccounts]);

  // Update selected gateway when settings are loaded
  useEffect(() => {
    const paymentGatewaySetting = allSettings.find(setting => setting.name === 'PAYMENT_GATEWAY');
    if (paymentGatewaySetting) {
      const gatewayValue = paymentGatewaySetting.value || 'paymob';
      setSelectedGateway(gatewayValue.toLowerCase());
    }
  }, [allSettings]);

  // Update account objects from settings when both settings and accounts are loaded
  useEffect(() => {
    if (accountsList.length === 0) return;

    const paymobAccountSetting = allSettings.find(setting => setting.name === 'PAYMOB_ACCOUNT_NUMBER_ID');
    const thawaniAccountSetting = allSettings.find(setting => setting.name === 'THAWANI_ACCOUNT_NUMBER_ID');

    if (paymobAccountSetting && paymobAccountSetting.value) {
      const accountId = parseInt(paymobAccountSetting.value);
      const account = accountsList.find(acc => acc.id === accountId);
      if (account) {
        setPaymobAccount(account);
      } else {
        setPaymobAccount(null);
      }
    } else {
      setPaymobAccount(null);
    }

    if (thawaniAccountSetting && thawaniAccountSetting.value) {
      const accountId = parseInt(thawaniAccountSetting.value);
      const account = accountsList.find(acc => acc.id === accountId);
      if (account) {
        setThawaniAccount(account);
      } else {
        setThawaniAccount(null);
      }
    } else {
      setThawaniAccount(null);
    }
  }, [allSettings, accountsList]);

  // Helper function to format setting names for better readability
  const formatSettingName = (name) => {
    if (!name) return '';
    
    const acronyms = ['API', 'SID', 'URL', 'HMAC', 'SMS', 'ID', 'UID', 'UUID'];
    
    const parts = name.split('_').map(part => {
      const upperPart = part.toUpperCase();
      
      if (acronyms.includes(upperPart)) {
        return upperPart;
      }
      
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    });
    
    return parts.join(' ');
  };

  // Filter settings for Payment settings category
  const getPaymentSettings = () => {
    const paymentSettingNames = [
      'PAYMOB_HMAC_SECRET',
      'PAYMOB_PUBLIC_KEY',
      'PAYMOB_SECRET_KEY',
      'PAYMOB_API_KEY',
      'PAYMOB_PAYMENT_METHOD_IDS',
      'THAWANI_WEBHOOK_SECRET_KEY',
      'THAWANI_SECRET_KEY',
      'THAWANI_PUBLIC_KEY',
    ];

    return allSettings.filter(setting => paymentSettingNames.includes(setting.name));
  };

  // Categorize payment settings into subsections based on selected gateway
  const categorizePaymentSettings = (settings) => {
    const categorized = {
      'Paymob Settings': [],
      'Thawani Settings': []
    };

    settings.forEach(setting => {
      if (setting.name.startsWith('PAYMOB_')) {
        categorized['Paymob Settings'].push(setting);
      } else if (setting.name.startsWith('THAWANI_')) {
        categorized['Thawani Settings'].push(setting);
      }
    });

    // Filter based on selected gateway
    if (selectedGateway === 'paymob') {
      categorized['Thawani Settings'] = [];
    } else if (selectedGateway === 'thawani') {
      categorized['Paymob Settings'] = [];
    }

    return categorized;
  };

  // Handle payment gateway change (only update local state, don't save)
  const handleGatewayChange = (event, newValue) => {
    if (!newValue) return;
    setSelectedGateway(newValue);
  };

  const validatePaymobPaymentMethodIds = (value) => {
    if (value == null || String(value).trim() === '') {
      return null;
    }
    const trimmed = String(value).trim();
    if (!/^[\d,\s]+$/.test(trimmed)) {
      return t('settingsPages.paymentSettings.paymobMethodIdsInvalidChars');
    }
    const parts = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
    for (const p of parts) {
      if (!/^\d+$/.test(p)) {
        return t('settingsPages.paymentSettings.paymobMethodIdsInvalidSegment');
      }
    }
    return null;
  };

  const getPaymentFieldMeta = (setting) => {
    if (setting.name === 'PAYMOB_PAYMENT_METHOD_IDS') {
      return {
        title: t('settingsPages.paymentSettings.paymobPaymentMethodIdsLabel'),
        description: t('settingsPages.paymentSettings.paymobPaymentMethodIdsHelp'),
        placeholder: t('settingsPages.paymentSettings.paymobPaymentMethodIdsPlaceholder'),
      };
    }
    return {
      title: formatSettingName(setting.name),
      description: setting.description || null,
      placeholder: t('settingsPages.common.enterSettingValue'),
    };
  };

  // Handle save all settings (gateway + payment settings + account settings)
  const handleSaveAll = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    const paymentGatewaySetting = allSettings.find(setting => setting.name === 'PAYMENT_GATEWAY');
    const paymobAccountSetting = allSettings.find(setting => setting.name === 'PAYMOB_ACCOUNT_NUMBER_ID');
    const thawaniAccountSetting = allSettings.find(setting => setting.name === 'THAWANI_ACCOUNT_NUMBER_ID');
    
    // Check if there are any changes
    const hasGatewayChanges = paymentGatewaySetting && selectedGateway !== (paymentGatewaySetting.value || 'paymob').toLowerCase();
    
    const hasPaymentSettingsChanges = paymentSettings.some(setting => {
      const editedValue = editingValues[setting.id];
      return editedValue !== undefined && editedValue !== setting.value;
    });

    // Check account changes
    const currentPaymobAccountId = (paymobAccountSetting && paymobAccountSetting.value) ? paymobAccountSetting.value : '';
    const newPaymobAccountId = paymobAccount ? paymobAccount.id.toString() : '';
    const hasPaymobAccountChanges = currentPaymobAccountId !== newPaymobAccountId;

    const currentThawaniAccountId = (thawaniAccountSetting && thawaniAccountSetting.value) ? thawaniAccountSetting.value : '';
    const newThawaniAccountId = thawaniAccount ? thawaniAccount.id.toString() : '';
    const hasThawaniAccountChanges = currentThawaniAccountId !== newThawaniAccountId;

    if (!hasGatewayChanges && !hasPaymentSettingsChanges && !hasPaymobAccountChanges && !hasThawaniAccountChanges) {
      setResponseMessage(t('settingsPages.common.noChangesToSave'));
      setFailedNotification(true);
      return;
    }

    const paymobMethodIdsSetting = allSettings.find((s) => s.name === 'PAYMOB_PAYMENT_METHOD_IDS');
    if (paymobMethodIdsSetting) {
      const effective =
        editingValues[paymobMethodIdsSetting.id] !== undefined
          ? editingValues[paymobMethodIdsSetting.id]
          : paymobMethodIdsSetting.value;
      const err = validatePaymobPaymentMethodIds(effective);
      if (err) {
        setPaymobMethodIdsError(err);
        setResponseMessage(err);
        setFailedNotification(true);
        return;
      }
    }

    setSavingAll(true);
    
    try {
      const savePromises = [];

      // Save gateway if changed
      if (hasGatewayChanges && paymentGatewaySetting) {
        const settingData = {
          name: 'PAYMENT_GATEWAY',
          value: selectedGateway,
          description: paymentGatewaySetting.description || ''
        };
        savePromises.push(updateSettingById(paymentGatewaySetting.id, settingData));
      }

      // Save all payment settings that have changed
      paymentSettings.forEach(setting => {
        const editedValue = editingValues[setting.id];
        if (editedValue !== undefined && editedValue !== setting.value) {
          const valueToSave =
            setting.name === 'PAYMOB_PAYMENT_METHOD_IDS'
              ? String(editedValue).trim()
              : editedValue;
          const settingData = {
            name: setting.name,
            value: valueToSave,
            description: setting.description || ''
          };
          savePromises.push(updateSettingById(setting.id, settingData));
        }
      });

      // Save Paymob account if changed
      if (hasPaymobAccountChanges && paymobAccountSetting) {
        const settingData = {
          name: 'PAYMOB_ACCOUNT_NUMBER_ID',
          value: newPaymobAccountId,
          description: paymobAccountSetting.description || ''
        };
        savePromises.push(updateSettingById(paymobAccountSetting.id, settingData));
      } else if (hasPaymobAccountChanges && !paymobAccountSetting) {
        // If setting doesn't exist, we might need to create it, but for now just log
        console.warn('PAYMOB_ACCOUNT_NUMBER_ID setting not found');
      }

      // Save Thawani account if changed
      if (hasThawaniAccountChanges && thawaniAccountSetting) {
        const settingData = {
          name: 'THAWANI_ACCOUNT_NUMBER_ID',
          value: newThawaniAccountId,
          description: thawaniAccountSetting.description || ''
        };
        savePromises.push(updateSettingById(thawaniAccountSetting.id, settingData));
      } else if (hasThawaniAccountChanges && !thawaniAccountSetting) {
        // If setting doesn't exist, we might need to create it, but for now just log
        console.warn('THAWANI_ACCOUNT_NUMBER_ID setting not found');
      }

      await Promise.all(savePromises);
      
      // Update local state
      setAllSettings(prevSettings => {
        const updated = prevSettings.map(setting => {
          // Update gateway
          if (hasGatewayChanges && paymentGatewaySetting && setting.id === paymentGatewaySetting.id) {
            return { ...setting, value: selectedGateway };
          }
          // Update payment settings
          const editedValue = editingValues[setting.id];
          if (editedValue !== undefined) {
            const nextValue =
              setting.name === 'PAYMOB_PAYMENT_METHOD_IDS'
                ? String(editedValue).trim()
                : editedValue;
            return { ...setting, value: nextValue };
          }
          // Update Paymob account
          if (hasPaymobAccountChanges && paymobAccountSetting && setting.id === paymobAccountSetting.id) {
            return { ...setting, value: newPaymobAccountId };
          }
          // Update Thawani account
          if (hasThawaniAccountChanges && thawaniAccountSetting && setting.id === thawaniAccountSetting.id) {
            return { ...setting, value: newThawaniAccountId };
          }
          return setting;
        });
        return updated;
      });
      
      // Clear editing values
      setEditingValues({});
      setPaymobMethodIdsError(null);

      setResponseMessage(t('settingsPages.paymentSettings.updatedSuccess'));
      setSuccessNotification(true);
    } catch (error) {
      console.error('Failed to save settings:', error);
      const errorMessage = translateAxiosErrorMessage(error, t) || t('settingsPages.common.saveFailed');
      setResponseMessage(errorMessage);
      setFailedNotification(true);
      
      // Revert gateway selection on error
      if (hasGatewayChanges && paymentGatewaySetting) {
        const currentValue = paymentGatewaySetting.value || 'paymob';
        setSelectedGateway(currentValue.toLowerCase());
      }
    } finally {
      setSavingAll(false);
    }
  };

  // Handle setting value change
  const handleSettingValueChange = (settingId, value, options = {}) => {
    if (options?.clearPaymobMethodIdsError) {
      setPaymobMethodIdsError(null);
    }
    setEditingValues(prev => ({
      ...prev,
      [settingId]: value
    }));
  };

  const paymentSettings = getPaymentSettings();
  const categorizedSettings = categorizePaymentSettings(paymentSettings);

  // Check if gateway has changed
  const paymentGatewaySetting = allSettings.find(setting => setting.name === 'PAYMENT_GATEWAY');
  const currentGatewayValue = paymentGatewaySetting ? (paymentGatewaySetting.value || 'paymob').toLowerCase() : 'paymob';
  const hasGatewayChanges = selectedGateway !== currentGatewayValue;

  // Check if accounts have changed
  const paymobAccountSetting = allSettings.find(setting => setting.name === 'PAYMOB_ACCOUNT_NUMBER_ID');
  const thawaniAccountSetting = allSettings.find(setting => setting.name === 'THAWANI_ACCOUNT_NUMBER_ID');
  const currentPaymobAccountId = (paymobAccountSetting && paymobAccountSetting.value) ? paymobAccountSetting.value : '';
  const newPaymobAccountId = paymobAccount ? paymobAccount.id.toString() : '';
  const hasPaymobAccountChanges = currentPaymobAccountId !== newPaymobAccountId;
  const currentThawaniAccountId = (thawaniAccountSetting && thawaniAccountSetting.value) ? thawaniAccountSetting.value : '';
  const newThawaniAccountId = thawaniAccount ? thawaniAccount.id.toString() : '';
  const hasThawaniAccountChanges = currentThawaniAccountId !== newThawaniAccountId;

  const subsectionLabel = (name) => {
    if (name === 'Paymob Settings') return t('settingsPages.paymentSettings.paymobSection');
    if (name === 'Thawani Settings') return t('settingsPages.paymentSettings.thawaniSection');
    return name;
  };

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
        <Box component="main" sx={{ overflow: "auto", pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <MuiTypography variant="h4" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                {t('settingsPages.paymentSettings.pageTitle')}
              </MuiTypography>
              <MuiTypography variant="body1" color="#6B7280" sx={{ fontSize: '1.1rem' }}>
                {t('settingsPages.paymentSettings.pageSubtitle')}
              </MuiTypography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
              <Paper 
                elevation={0} 
                sx={{ 
                  mb: 4,
                  borderRadius: 3,
                  border: '1px solid #E5E7EB',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: '#FEF3C7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}
                      >
                        <PaymentIcon sx={{ color: '#D97706', fontSize: 24 }} />
                      </Box>
                      <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        {t('settingsPages.paymentSettings.cardTitle')}
                      </MuiTypography>
                    </Box>
                    <Divider />
                  </Box>

                  {/* Payment Gateway Selector */}
                  <Box sx={{ mb: 4 }}>
                    <FormLabel
                      sx={{
                        fontWeight: 600,
                        color: '#1F2937',
                        marginBottom: '8px',
                        fontSize: '0.95rem',
                      }}
                    >
                      {t('settingsPages.paymentSettings.gatewayLabel')}
                    </FormLabel>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Select
                        value={selectedGateway}
                        onChange={handleGatewayChange}
                        disabled={savingAll || settingsLoading}
                        placeholder={t('settingsPages.paymentSettings.gatewayPlaceholder')}
                        sx={{
                          flexGrow: 1,
                          backgroundColor: '#FFFFFF',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          '&:hover': {
                            borderColor: '#D97706',
                          },
                          '&:focus-within': {
                            borderColor: '#D97706',
                            boxShadow: '0 0 0 3px rgba(217, 119, 6, 0.1)',
                          },
                          '&[disabled]': {
                            backgroundColor: '#F3F4F6',
                            color: '#6B7280',
                            cursor: 'not-allowed',
                          },
                        }}
                      >
                      <Option value="paymob">{t('settingsPages.paymentSettings.gatewayPaymob')}</Option>
                      <Option value="thawani">{t('settingsPages.paymentSettings.gatewayThawani')}</Option>
                    </Select>
                    </Box>
                  </Box>

                  {settingsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : paymentSettings.length === 0 ? (
                    <Alert severity="info">
                      {t('settingsPages.paymentSettings.empty')}
                    </Alert>
                  ) : (
                    <Box>
                      {/* Account Selection - Based on Selected Gateway */}
                      <Box sx={{ mb: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4B5563', fontSize: '0.95rem' }}>
                            {t('settingsPages.paymentSettings.defaultAccount')}
                          </Typography>
                        </Box>
                        {selectedGateway === 'paymob' && (
                          <Paper 
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #F3F4F6',
                              background: '#FFFFFF',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                              }
                            }}
                          >
                            <AccountOptionList
                              label={t('settingsPages.paymentSettings.defaultPaymob')}
                              currentAccount={paymobAccount}
                              setCurrentAccount={setPaymobAccount}
                              disabled={savingAll || settingsLoading}
                              showActiveOnly={true}
                            />
                          </Paper>
                        )}
                        {selectedGateway === 'thawani' && (
                          <Paper 
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #F3F4F6',
                              background: '#FFFFFF',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                              }
                            }}
                          >
                            <AccountOptionList
                              label={t('settingsPages.paymentSettings.defaultThawani')}
                              currentAccount={thawaniAccount}
                              setCurrentAccount={setThawaniAccount}
                              disabled={savingAll || settingsLoading}
                              showActiveOnly={true}
                            />
                          </Paper>
                        )}
                      </Box>

                      {Object.entries(categorizedSettings).map(([subsectionName, settings]) => {
                        if (settings.length === 0) return null;

                        return (
                          <Box key={subsectionName} sx={{ mb: 4 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4B5563', fontSize: '0.95rem' }}>
                                {subsectionLabel(subsectionName)}
                              </Typography>
                            </Box>

                            <Grid container spacing={2}>
                              {settings.map((setting) => {
                                const currentValue = editingValues[setting.id] !== undefined 
                                  ? editingValues[setting.id] 
                                  : (setting.value || '');
                                const fieldMeta = getPaymentFieldMeta(setting);
                                const isPaymobMethodIds = setting.name === 'PAYMOB_PAYMENT_METHOD_IDS';
                                
                                return (
                                  <Grid item xs={12} key={setting.id}>
                                    <Paper 
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid #F3F4F6',
                                        background: '#FFFFFF',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                          transform: 'translateY(-2px)',
                                          border: '1px solid #E5E7EB',
                                          boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                                        }
                                      }}
                                    >
                                      <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.5 }}>
                                          {fieldMeta.title}
                                        </Typography>
                                        {fieldMeta.description && (
                                          <Typography variant="body2" color="#6B7280" sx={{ fontSize: '0.875rem' }}>
                                            {fieldMeta.description}
                                          </Typography>
                                        )}
                                      </Box>
                                      
                                      <Input
                                        fullWidth
                                        value={currentValue}
                                        onChange={(e) => {
                                          if (isPaymobMethodIds) {
                                            handleSettingValueChange(setting.id, e.target.value, { clearPaymobMethodIdsError: true });
                                          } else {
                                            handleSettingValueChange(setting.id, e.target.value);
                                          }
                                        }}
                                        onBlur={
                                          isPaymobMethodIds
                                            ? () => setPaymobMethodIdsError(validatePaymobPaymentMethodIds(currentValue))
                                            : undefined
                                        }
                                        placeholder={fieldMeta.placeholder}
                                        disabled={savingAll}
                                        color={isPaymobMethodIds && paymobMethodIdsError ? 'danger' : 'neutral'}
                                        sx={{ 
                                          '& input': {
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem'
                                          }
                                        }}
                                      />
                                      {isPaymobMethodIds && paymobMethodIdsError && (
                                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                                          {paymobMethodIdsError}
                                        </Typography>
                                      )}
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        );
                      })}
                      
                      {/* Single Save Button for All Settings */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 3, borderTop: '1px solid #E5E7EB' }}>
                        <PermissionGate permissions={[PERMISSIONS.SETTINGS_MANAGE]}>
                          <Button
                            type="button"
                            variant="contained"
                            size="md"
                            startDecorator={savingAll ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <SaveIcon />}
                            onClick={handleSaveAll}
                            disabled={savingAll || settingsLoading || !!paymobMethodIdsError || (!hasGatewayChanges && !hasPaymobAccountChanges && !hasThawaniAccountChanges && !paymentSettings.some(setting => {
                              const editedValue = editingValues[setting.id];
                              return editedValue !== undefined && editedValue !== setting.value;
                            }))}
                            sx={{ minWidth: 150, px: 3 }}
                          >
                            {savingAll ? t('settingsPages.common.saving') : t('actions.save')}
                          </Button>
                        </PermissionGate>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Paper>
            </PermissionGate>

            {/* Success Notification */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setSuccessNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>

            {/* Error Notification */}
            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setFailedNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>
          </Container>
        </Box>
      </Box>
    </DirectionalMaterialThemeProvider>
  );
}

